/**
 * Session-scoped draft persistence for Provider Onboarding (VendorModal wizard).
 */

import type {
  AuthType,
  LiveTestResult,
  RequestBodyType,
  RoutingServiceKey,
  VendorEndpointMapping,
  VendorMapping,
} from '../domain/vendor';
import { normalizeConfigLabel } from '../lib/adapterCapabilityCatalog';

export const VENDOR_ONBOARDING_DRAFT_STORAGE_KEY = 'evolveos:vendor-onboarding-draft:v1';
export const VENDOR_ONBOARDING_STEP_LABELS = ['Connect', 'Request', 'Live Test', 'Response Mapping'] as const;

export type VendorOnboardingMode = 'vendor' | 'add-api' | 'edit-api';

export interface VendorOnboardingHeaderRow {
  rowId: string;
  key: string;
  value: string;
}

export interface VendorOnboardingBodyBuilderRow {
  rowId: string;
  path: string;
  value: string;
}

export interface VendorOnboardingRequestMappingRow {
  rowId: string;
  path: string;
  expression: string;
}

export interface VendorOnboardingDraftSnapshot {
  step: number;
  selectedConfigLabel: string;
  vendorName: string;
  vendorId: string;
  baseUrl: string;
  serviceName: RoutingServiceKey;
  selectedCatalogGroupKey: string | null;
  capability: string;
  authType: AuthType;
  authConfig: Record<string, string>;
  providerVariables: Record<string, string>;
  requestMethod: string;
  requestPath: string;
  endpointTimeoutMs: number | '';
  endpointMaxRetries: number | '';
  endpointRetryDelayMs: number | '';
  endpointBodyFormat: RequestBodyType | '';
  headerRows: VendorOnboardingHeaderRow[];
  showBody: boolean;
  bodyMode: RequestBodyType;
  bodyText: string;
  bodyBuilderRows: VendorOnboardingBodyBuilderRow[];
  formBodyRows: VendorOnboardingHeaderRow[];
  requestMappingRows: VendorOnboardingRequestMappingRow[];
  requestMappingText: string;
  requestMappingSampleText: string;
  requestMappingBuilderActive: boolean;
  mappingPaths: Record<string, string>;
  savedEndpointMappings: VendorEndpointMapping[];
  liveResult: LiveTestResult | null;
  showCanonicalRequest: boolean;
  showCanonicalResponse: boolean;
}

export interface VendorOnboardingDraftContext {
  mode: VendorOnboardingMode;
  vendorId: string | null;
  endpointCapability: string | null;
  configLabel: string;
}

export interface VendorOnboardingDraftMeta extends VendorOnboardingDraftContext {
  draftKey: string;
  vendorName: string;
  step: number;
  updatedAt: string;
}

export interface StoredVendorOnboardingDraft {
  meta: VendorOnboardingDraftMeta;
  snapshot: VendorOnboardingDraftSnapshot;
}

type DraftStore = Record<string, StoredVendorOnboardingDraft>;

let inMemoryDraftStore: DraftStore | null = null;

/** Use in-memory storage for unit tests (returns cleanup). */
export function useInMemoryDraftStoreForTests(): () => void {
  inMemoryDraftStore = {};
  return () => {
    inMemoryDraftStore = null;
  };
}

/** Prefer saved vendor id; fall back to wizard-typed id during new-vendor onboarding. */
export function effectiveDraftVendorId(
  ctxVendorId: string | null | undefined,
  snapshotVendorId: string | null | undefined,
): string | null {
  const fromCtx = ctxVendorId?.trim();
  if (fromCtx) return fromCtx;
  const fromSnapshot = snapshotVendorId?.trim();
  return fromSnapshot || null;
}

export function buildDraftKey(ctx: VendorOnboardingDraftContext): string {
  const vendorSegment = ctx.vendorId?.trim() || 'new';
  const endpointSegment = ctx.endpointCapability?.trim() || 'none';
  const labelSegment = ctx.configLabel.trim() || 'default';
  return `${ctx.mode}:${vendorSegment}:${endpointSegment}:${labelSegment}`;
}

function draftMatchesVendorSegment(
  draft: StoredVendorOnboardingDraft,
  vendorSegment: string,
): boolean {
  if (draft.meta.mode === 'vendor' && !draft.meta.vendorId?.trim()) {
    return vendorSegment === 'new';
  }
  const effectiveVendor = effectiveDraftVendorId(draft.meta.vendorId, draft.snapshot.vendorId);
  const metaVendor = effectiveVendor?.trim() || 'new';
  return metaVendor === vendorSegment;
}

function endpointHasSavedMapping(endpoint: VendorEndpointMapping): boolean {
  if (endpoint.fieldMappings && endpoint.fieldMappings.length > 0) return true;
  return Boolean(endpoint.responseMapping && Object.keys(endpoint.responseMapping).length > 0);
}

function isDraftObsoleteForVendors(
  draft: StoredVendorOnboardingDraft,
  vendors: VendorMapping[],
): boolean {
  const vid = effectiveDraftVendorId(draft.meta.vendorId, draft.snapshot.vendorId);
  if (!vid) return false;
  const vendor = vendors.find(v => v.vendorId === vid);
  if (!vendor) return false;

  const endpoints = vendor.endpointMappings ?? [];
  const cap = draft.meta.endpointCapability?.trim();

  if (draft.meta.mode === 'vendor' && endpoints.length > 0) return true;

  if (draft.meta.mode === 'edit-api' && cap) {
    const ep = endpoints.find(e => e.capability === cap);
    return ep ? endpointHasSavedMapping(ep) : false;
  }

  if (draft.meta.mode === 'add-api' && cap) {
    return endpoints.some(e => e.capability === cap);
  }

  return false;
}

function isCorruptOnboardingDraft(draft: StoredVendorOnboardingDraft): boolean {
  const meta = draft.meta;
  if (meta.mode === 'edit-api' || meta.mode === 'add-api') return true;
  return false;
}

/** Remove edit-api drafts and add-api drafts saved without an endpoint (corrupt :none: keys). */
export function pruneCorruptOnboardingDrafts(): void {
  const store = readStore();
  let changed = false;

  for (const [key, draft] of Object.entries(store)) {
    if (isCorruptOnboardingDraft(draft)) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) writeStore(store);
}

export function hasMeaningfulProgress(snapshot: VendorOnboardingDraftSnapshot): boolean {
  if (snapshot.step > 0) return true;
  if (snapshot.vendorId.trim()) return true;
  if (snapshot.vendorName.trim()) return true;
  if (snapshot.baseUrl.trim()) return true;
  if (snapshot.authType !== 'none' || Object.values(snapshot.authConfig).some(v => v.trim())) return true;
  if (Object.values(snapshot.providerVariables ?? {}).some(v => v.trim())) return true;
  if (snapshot.requestPath.trim()) return true;
  if (snapshot.headerRows.some(r => r.key.trim() || r.value.trim())) return true;
  if (snapshot.showBody && snapshot.bodyText.trim() !== '{\n  \n}') return true;
  if (snapshot.requestMappingText.trim()) return true;
  if (Object.keys(snapshot.mappingPaths).length > 0) return true;
  if (snapshot.savedEndpointMappings.length > 0) return true;
  if (snapshot.liveResult !== null) return true;
  return false;
}

function readStore(): DraftStore {
  if (inMemoryDraftStore !== null) return inMemoryDraftStore;
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(VENDOR_ONBOARDING_DRAFT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as DraftStore;
  } catch {
    return {};
  }
}

function writeStore(store: DraftStore): void {
  if (inMemoryDraftStore !== null) {
    inMemoryDraftStore = { ...store };
    return;
  }
  if (typeof window === 'undefined') return;
  if (Object.keys(store).length === 0) {
    window.sessionStorage.removeItem(VENDOR_ONBOARDING_DRAFT_STORAGE_KEY);
    return;
  }
  window.sessionStorage.setItem(VENDOR_ONBOARDING_DRAFT_STORAGE_KEY, JSON.stringify(store));
}

export function loadVendorOnboardingDraft(draftKey: string): StoredVendorOnboardingDraft | null {
  const store = readStore();
  return store[draftKey] ?? null;
}

export function findDraftForContext(ctx: {
  mode: VendorOnboardingMode;
  vendorId: string | null;
  endpointCapability: string | null;
  configLabel?: string;
}): StoredVendorOnboardingDraft | null {
  const store = readStore();
  const vendorSegment = ctx.vendorId?.trim() || 'new';
  const endpointSegment = ctx.endpointCapability?.trim() || 'none';
  const labelSegment = ctx.configLabel?.trim() ? ctx.configLabel.trim() : null;

  let latest: StoredVendorOnboardingDraft | null = null;
  for (const draft of Object.values(store)) {
    const meta = draft.meta;
    const metaEndpoint = meta.endpointCapability?.trim() || 'none';
    if (meta.mode !== ctx.mode || metaEndpoint !== endpointSegment) continue;
    if (labelSegment && normalizeConfigLabel(meta.configLabel ?? '') !== normalizeConfigLabel(labelSegment)) {
      continue;
    }
    if (!draftMatchesVendorSegment(draft, vendorSegment)) continue;
    if (!latest || meta.updatedAt > latest.meta.updatedAt) {
      latest = draft;
    }
  }
  return latest;
}

export function saveVendorOnboardingDraft(
  draftKey: string,
  meta: Omit<VendorOnboardingDraftMeta, 'draftKey' | 'updatedAt'>,
  snapshot: VendorOnboardingDraftSnapshot,
): void {
  if (!hasMeaningfulProgress(snapshot)) {
    clearVendorOnboardingDraft(draftKey);
    return;
  }

  const resolvedVendorId =
    meta.vendorId?.trim() != null && meta.vendorId.trim() !== ''
      ? effectiveDraftVendorId(meta.vendorId, snapshot.vendorId)
      : meta.mode === 'vendor'
        ? null
        : effectiveDraftVendorId(meta.vendorId, snapshot.vendorId);

  const store = readStore();
  store[draftKey] = {
    meta: {
      ...meta,
      vendorId: resolvedVendorId,
      draftKey,
      step: snapshot.step,
      vendorName: snapshot.vendorName,
      updatedAt: new Date().toISOString(),
    },
    snapshot,
  };
  writeStore(store);
}

export function clearVendorOnboardingDraft(draftKey: string): void {
  const store = readStore();
  if (!(draftKey in store)) return;
  delete store[draftKey];
  writeStore(store);
}

/** Remove an in-progress onboarding draft (list page or modal discard). */
export function discardVendorOnboardingDraft(meta: VendorOnboardingDraftMeta): void {
  clearVendorOnboardingDraft(meta.draftKey);
  clearDraftsForContext({
    mode: meta.mode,
    vendorId: meta.vendorId,
    endpointCapability: meta.endpointCapability,
  });
}

export function clearDraftsForContext(ctx: {
  mode: VendorOnboardingMode;
  vendorId: string | null;
  endpointCapability: string | null;
}): void {
  const store = readStore();
  const vendorSegment = ctx.vendorId?.trim() || 'new';
  const endpointSegment = ctx.endpointCapability?.trim() || 'none';
  let changed = false;

  for (const [key, draft] of Object.entries(store)) {
    const meta = draft.meta;
    const metaEndpoint = meta.endpointCapability?.trim() || 'none';
    if (meta.mode === ctx.mode && metaEndpoint === endpointSegment && draftMatchesVendorSegment(draft, vendorSegment)) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) writeStore(store);
}

/** Remove all drafts tied to a vendor id (meta, snapshot, or draft key segment). */
export function clearDraftsForVendorId(vendorId: string): void {
  const segment = vendorId.trim();
  if (!segment) return;

  const store = readStore();
  let changed = false;

  for (const [key, draft] of Object.entries(store)) {
    const metaVid = draft.meta.vendorId?.trim();
    const snapVid = draft.snapshot.vendorId.trim();
    if (key.includes(`:${segment}:`) || metaVid === segment || snapVid === segment) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) writeStore(store);
}

export function pruneObsoleteOnboardingDrafts(vendors: VendorMapping[]): void {
  if (vendors.length === 0) return;

  const store = readStore();
  let changed = false;

  for (const [key, draft] of Object.entries(store)) {
    if (isDraftObsoleteForVendors(draft, vendors)) {
      delete store[key];
      changed = true;
    }
  }

  if (changed) writeStore(store);
}

export function resolveDraftVendorIdForResume(meta: VendorOnboardingDraftMeta): string | null {
  const stored = loadVendorOnboardingDraft(meta.draftKey);
  return effectiveDraftVendorId(meta.vendorId, stored?.snapshot.vendorId ?? null);
}

export function isOnboardingDraftActionable(
  draft: VendorOnboardingDraftMeta,
  vendors: VendorMapping[] | undefined,
): boolean {
  if (draft.mode === 'edit-api') return false;
  if (!vendors?.length) return true;
  const stored = loadVendorOnboardingDraft(draft.draftKey);
  if (!stored) return false;
  if (isCorruptOnboardingDraft(stored)) return false;
  return !isDraftObsoleteForVendors(stored, vendors);
}

export function listVendorOnboardingDraftSummaries(): VendorOnboardingDraftMeta[] {
  const store = readStore();
  return Object.values(store)
    .map(d => d.meta)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function findLatestVendorOnboardingDraft(): VendorOnboardingDraftMeta | null {
  return listVendorOnboardingDraftSummaries()[0] ?? null;
}

export interface VendorModalDraftSetters {
  setStep: (step: number) => void;
  setSelectedConfigLabel: (label: string) => void;
  setVendorName: (value: string) => void;
  setVendorId: (value: string) => void;
  setBaseUrl: (value: string) => void;
  setServiceName: (value: RoutingServiceKey) => void;
  setSelectedCatalogGroupKey: (value: string | null) => void;
  setCapability: (value: string) => void;
  setAuthType: (value: AuthType) => void;
  setAuthConfig: (value: Record<string, string>) => void;
  setProviderVariables: (value: Record<string, string>) => void;
  setRequestMethod: (value: string) => void;
  setRequestPath: (value: string) => void;
  setEndpointTimeoutMs: (value: number | '') => void;
  setEndpointMaxRetries: (value: number | '') => void;
  setEndpointRetryDelayMs: (value: number | '') => void;
  setEndpointBodyFormat: (value: RequestBodyType | '') => void;
  setHeaderRows: (value: VendorOnboardingHeaderRow[]) => void;
  setShowBody: (value: boolean) => void;
  setBodyMode: (value: RequestBodyType) => void;
  setBodyText: (value: string) => void;
  setBodyBuilderRows: (value: VendorOnboardingBodyBuilderRow[]) => void;
  setFormBodyRows: (value: VendorOnboardingHeaderRow[]) => void;
  setRequestMappingRows: (value: VendorOnboardingRequestMappingRow[]) => void;
  setRequestMappingText: (value: string) => void;
  setRequestMappingSampleText: (value: string) => void;
  setRequestMappingBuilderActive: (value: boolean) => void;
  setMappingPaths: (value: Record<string, string>) => void;
  setSavedEndpointMappings: (value: VendorEndpointMapping[]) => void;
  setLiveResult: (value: LiveTestResult | null) => void;
  setShowCanonicalRequest: (value: boolean) => void;
  setShowCanonicalResponse: (value: boolean) => void;
}

export function applyVendorModalDraft(
  snapshot: VendorOnboardingDraftSnapshot,
  setters: VendorModalDraftSetters,
): void {
  setters.setStep(snapshot.step);
  setters.setSelectedConfigLabel(snapshot.selectedConfigLabel);
  setters.setVendorName(snapshot.vendorName);
  setters.setVendorId(snapshot.vendorId);
  setters.setBaseUrl(snapshot.baseUrl);
  setters.setServiceName(snapshot.serviceName);
  setters.setSelectedCatalogGroupKey(snapshot.selectedCatalogGroupKey);
  setters.setCapability(snapshot.capability);
  setters.setAuthType(snapshot.authType);
  setters.setAuthConfig(snapshot.authConfig);
  setters.setProviderVariables(snapshot.providerVariables ?? {});
  setters.setRequestMethod(snapshot.requestMethod);
  setters.setRequestPath(snapshot.requestPath);
  setters.setEndpointTimeoutMs(snapshot.endpointTimeoutMs);
  setters.setEndpointMaxRetries(snapshot.endpointMaxRetries);
  setters.setEndpointRetryDelayMs(snapshot.endpointRetryDelayMs);
  setters.setEndpointBodyFormat(snapshot.endpointBodyFormat);
  setters.setHeaderRows(snapshot.headerRows);
  setters.setShowBody(snapshot.showBody);
  setters.setBodyMode(snapshot.bodyMode);
  setters.setBodyText(snapshot.bodyText);
  setters.setBodyBuilderRows(snapshot.bodyBuilderRows);
  setters.setFormBodyRows(snapshot.formBodyRows);
  setters.setRequestMappingRows(snapshot.requestMappingRows);
  setters.setRequestMappingText(snapshot.requestMappingText);
  setters.setRequestMappingSampleText(snapshot.requestMappingSampleText);
  setters.setRequestMappingBuilderActive(snapshot.requestMappingBuilderActive);
  setters.setMappingPaths(snapshot.mappingPaths);
  setters.setSavedEndpointMappings(snapshot.savedEndpointMappings);
  setters.setLiveResult(snapshot.liveResult);
  setters.setShowCanonicalRequest(snapshot.showCanonicalRequest);
  setters.setShowCanonicalResponse(snapshot.showCanonicalResponse);
}
